import { GraphQLResult, V6Client } from "@aws-amplify/api-graphql";
import {
  CreateLocationMutation,
  Location,
  LocationSearchFilters,
  SearchLocationsQueryVariables,
} from "~/api/API";
import { searchLocations as searchLocationsQuery } from "~/api/graphql/queries";
import {
  createLocationReturningID,
  updateLocationReturningID,
} from "~/api/graphql-custom/mutations";
import { listLocationsOptimized } from "~/api/graphql-custom/queries";
import { graphql } from "~/lib/graphql-client";
import { emailValidation } from "~/pages/components/forms/appointments/assistant/validation/base";
import { LocationFormData } from "~/pages/locations/type";
import { buildAddressInput } from "./helpers/appointment-query-builder";

export class InvalidCalendarCcEmailsError extends Error {
  constructor(public readonly invalidEmails: string[]) {
    super("Invalid calendar CC email addresses");
    this.name = "InvalidCalendarCcEmailsError";
  }
}

const sanitizeCalendarCcEmails = (
  emails?: (string | null | undefined)[],
): string[] => {
  const sanitized = (emails || [])
    .map((email) => email?.trim())
    .filter((email): email is string => Boolean(email));

  const invalidEmails = sanitized.filter(
    (email) => !emailValidation.isValidSync(email),
  );

  if (invalidEmails.length > 0) {
    throw new InvalidCalendarCcEmailsError(invalidEmails);
  }

  return sanitized;
};

/**
 * Fetches all locations with pagination support.
 *
 * Uses the graphql wrapper which handles partial auth errors gracefully.
 * This is important because the query includes nested `doctors` which may
 * return Unauthorized errors for Doctor users (they can only see their own record).
 *
 * @param _client - V6Client (kept for backwards compatibility, not used)
 */
export const fetchAllLocations = async (
  _client: V6Client,
): Promise<Location[]> => {
  const fetchPage = async (
    nextToken?: string,
    accumulatedItems: Location[] = [],
  ): Promise<Location[]> => {
    // Use the graphql wrapper which handles partial auth errors gracefully
    // Fatal errors (validation, system) will still throw
    const { data } = await graphql(
      {
        query: listLocationsOptimized,
        variables: { nextToken },
      },
      { errorMode: "partial" },
    );

    const newItems = (data?.listLocations?.items || []) as Location[];
    const combinedItems = [...accumulatedItems, ...newItems];

    if (data?.listLocations?.nextToken) {
      return await fetchPage(data.listLocations.nextToken, combinedItems);
    } else {
      return combinedItems;
    }
  };

  const allLocations = await fetchPage();

  // Sort the locations by their 'name' field
  return allLocations.sort((a, b) => a.name?.localeCompare(b.name || "") || 0);
};

export const updateLocationInfo = async (
  client: V6Client,
  location: Location,
  formData: LocationFormData,
) => {
  const updateLocationPromise = client.graphql({
    query: updateLocationReturningID,
    variables: {
      input: {
        id: location.id,
        name: formData.locationInfo.content.name,
        abbreviation: formData.locationInfo.content.abbreviation,
        region: formData.locationInfo.content.region,
        status: formData.locationInfo.content.status,
        address: {
          ...buildAddressInput(formData.locationInfo.content.address),
          county: formData.locationInfo.content.county || null,
        },
        indigoOfficePhoneNumber:
          formData.locationInfo.content.indigoOfficePhoneNumber,
        isWaitingArea: formData.locationInfo.content.isWaitingArea,
        additionalRoomsNumber:
          formData.locationInfo.content.additionalRoomsNumber,
        landlordContactInfo: {
          name: formData.locationInfo.content.landlordContactInfo?.name,
          phone: formData.locationInfo.content.landlordContactInfo?.phone,
          email: formData.locationInfo.content.landlordContactInfo?.email,
        },
        calendarCcEmails: sanitizeCalendarCcEmails(
          formData.locationInfo.content.calendarCcEmails,
        ),
        isParkingFree: formData.logistics.content.isParkingFree,
        parkingRate: formData.logistics.content.parkingRate,
        isFrontDoorLocked: formData.logistics.content.isFrontDoorLocked,
        frontDoorCode: formData.logistics.content.frontDoorCode,
        backDoor: formData.logistics.content.backDoor,
        backDoorCode: formData.logistics.content.backDoorCode,
        securityUnlockingProcess:
          formData.logistics.content.securityUnlockingProcess,
        securityLockingProcess:
          formData.logistics.content.securityLockingProcess,
        maleBathroomCode: formData.logistics.content.maleBathroomCode,
        femaleBathroomCode: formData.logistics.content.femaleBathroomCode,
        wifiName: formData.logistics.content.wifiName,
        wifiPassword: formData.logistics.content.wifiPassword,
        locationDefaultOnsiteOpsId:
          formData.logistics.content.defaultOnsiteOpsId,
        locationSecondaryOnsiteOpsId:
          formData.logistics.content.secondaryOnsiteOpsId,
      },
    },
  });

  try {
    const locationResult = await updateLocationPromise;
    console.log("Location updated successfully:", locationResult);
    return { location: locationResult };
  } catch (error) {
    console.error("Error updating location info:", error);
    throw error;
  }
};

export interface LocationSearchParams {
  query: string;
  state?: string;
  region?: string;
  status?: string;
}

/**
 * Searches locations via the OpenSearch-backed `searchLocations` query.
 * Returns the (non-null) items from the connection response.
 */
export const searchLocations = async (params: LocationSearchParams) => {
  const filters: LocationSearchFilters = {
    ...(params.state ? { state: params.state } : {}),
    ...(params.region ? { region: params.region } : {}),
    ...(params.status ? { status: params.status } : {}),
  };

  const variables: SearchLocationsQueryVariables = {
    input: {
      query: params.query,
      from: 0,
      ...(Object.keys(filters).length > 0 ? { filters } : {}),
    },
  };

  const { data } = await graphql({
    query: searchLocationsQuery,
    variables,
  });

  const items = data?.searchLocations?.items;
  if (!items) {
    return [];
  }

  return items.filter(
    (item): item is NonNullable<typeof item> => item !== null,
  );
};

export const createLocation = async (
  client: V6Client,
  formData: LocationFormData,
): Promise<GraphQLResult<CreateLocationMutation>> => {
  const createLocationPromise = client.graphql({
    query: createLocationReturningID,
    variables: {
      input: {
        name: formData.locationInfo.content.name,
        abbreviation: formData.locationInfo.content.abbreviation,
        region: formData.locationInfo.content.region,
        status: formData.locationInfo.content.status,
        address: {
          ...buildAddressInput(formData.locationInfo.content.address),
          county: formData.locationInfo.content.county || null,
        },
        indigoOfficePhoneNumber:
          formData.locationInfo.content.indigoOfficePhoneNumber,
        isWaitingArea: formData.locationInfo.content.isWaitingArea,
        additionalRoomsNumber:
          formData.locationInfo.content.additionalRoomsNumber,
        landlordContactInfo: {
          name: formData.locationInfo.content.landlordContactInfo?.name,
          phone: formData.locationInfo.content.landlordContactInfo?.phone,
          email: formData.locationInfo.content.landlordContactInfo?.email,
        },
        calendarCcEmails: sanitizeCalendarCcEmails(
          formData.locationInfo.content.calendarCcEmails,
        ),
        isParkingFree: formData.logistics.content.isParkingFree,
        parkingRate: formData.logistics.content.parkingRate,
        isFrontDoorLocked: formData.logistics.content.isFrontDoorLocked,
        frontDoorCode: formData.logistics.content.frontDoorCode,
        backDoor: formData.logistics.content.backDoor,
        backDoorCode: formData.logistics.content.backDoorCode,
        securityUnlockingProcess:
          formData.logistics.content.securityUnlockingProcess,
        securityLockingProcess:
          formData.logistics.content.securityLockingProcess,
        maleBathroomCode: formData.logistics.content.maleBathroomCode,
        femaleBathroomCode: formData.logistics.content.femaleBathroomCode,
        wifiName: formData.logistics.content.wifiName,
        wifiPassword: formData.logistics.content.wifiPassword,
        locationDefaultOnsiteOpsId:
          formData.logistics.content.defaultOnsiteOpsId,
        locationSecondaryOnsiteOpsId:
          formData.logistics.content.secondaryOnsiteOpsId,
      },
    },
  });

  try {
    const locationResult = await createLocationPromise;
    if (locationResult.errors && locationResult.errors.length > 0) {
      throw new Error(
        `Failed to create location: ${locationResult.errors.map((e) => e.message).join(", ")}`,
      );
    }
    return locationResult;
  } catch (error) {
    console.error("Error creating location info:", error);
    throw error;
  }
};
